import type { Scene, SceneRevision } from '../api/client';

interface EarlierSceneReferencePickerProps {
  jobId: string;
  targetSceneId: string;
  scenes: Scene[];
  revisions: SceneRevision[];
  disabled: boolean;
  previewUrl: (imageUrl: string) => string;
  onSelect: (sourceSceneId: string) => void;
}

function isGeneratedSceneImage(imageUrl: string, jobId: string, revisionId: string): boolean {
  const value = imageUrl.trim();
  if (!value || /[\\%?#]/.test(value)) return false;
  let imagePath = value;
  if (/^https?:\/\//i.test(value)) {
    try {
      // Keep the original path so URL normalization cannot hide dot segments.
      const url = new URL(value);
      imagePath = value.match(/^https?:\/\/[^/]+(\/.*)?$/i)?.[1] || '';
      if (url.pathname !== imagePath) return false;
    } catch {
      return false;
    }
  }
  const key = imagePath.replace(/^\/?(?:api\/)?r2\//, '').replace(/^\//, '');
  if (key.split('/').some(segment => !segment || segment === '.' || segment === '..')) return false;
  // Generated images use the source revision ID in the immutable object path.
  // This is display eligibility; the server still authorizes sourceSceneId.
  return key.startsWith(`projects/${jobId}/scenes/${revisionId}/`);
}

function earlierStillScenes(jobId: string, targetSceneId: string, scenes: Scene[], revisions: SceneRevision[]) {
  const target = scenes.find(scene => scene.id === targetSceneId && scene.projectId === jobId);
  if (!target) return [];
  return scenes
    .filter(scene => scene.projectId === jobId && scene.id !== targetSceneId && scene.orderIndex < target.orderIndex)
    .flatMap(scene => {
      const current = revisions.find(revision => revision.id === scene.currentRevisionId && revision.sceneId === scene.id);
      return current?.imageUrl && !current.videoUrl && current.visualType !== 'video'
        && isGeneratedSceneImage(current.imageUrl, jobId, current.id)
        ? [{ sceneId: scene.id, orderIndex: scene.orderIndex, imageUrl: current.imageUrl }]
        : [];
    })
    .sort((left, right) => left.orderIndex - right.orderIndex);
}

export function EarlierSceneReferencePicker({ jobId, targetSceneId, scenes, revisions, disabled, previewUrl, onSelect }: EarlierSceneReferencePickerProps) {
  const choices = earlierStillScenes(jobId, targetSceneId, scenes, revisions);
  if (choices.length === 0) return null;
  return (
    <div className="space-y-1.5 pt-1">
      <div className="text-[11px] text-slate-400">หรือใช้ภาพนิ่งจากฉากก่อนหน้าในงานนี้เป็น Ref:</div>
      <div className="flex flex-wrap gap-2" aria-label="ภาพนิ่งจากฉากก่อนหน้า">
        {choices.map(choice => (
          <button key={choice.sceneId} type="button" title={`ใช้ภาพฉากที่ ${choice.orderIndex} เป็น Ref`}
            disabled={disabled} onClick={() => onSelect(choice.sceneId)}
            className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-900 px-2 py-1.5 text-[11px] text-slate-200 hover:border-sky-500 hover:bg-slate-800 disabled:opacity-50">
            <img src={previewUrl(choice.imageUrl)} alt={`ภาพฉากที่ ${choice.orderIndex}`} className="h-10 w-10 rounded object-cover" />
            <span>ฉากที่ {choice.orderIndex}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
